<template>
  <RevDrawer
    :back-button-label="i18n(modalBuybackTranslations.backButtonLabel)"
    :close-button-label="i18n(modalBuybackTranslations.close)"
    :name
    :title="i18n(translations.modalTitle)"
    @close="emit('close')"
  >
    <template #body v-if="counterOfferData">
      <h2 class="heading-1 mb-16">{{ title }}</h2>
      <ul class="list-none divide-y focus:outline-none">
        <li
          v-for="{ id: reasonId, label } in counterOfferData.reasonsList"
          :key="reasonId"
          class="body-1 relative list-none py-20 tap-highlight-transparent only:border-0 focus:outline-none"
        >
          {{ label }}
        </li>
      </ul>

      <div v-if="hasImages" class="mb-24" data-test="reasons-carousel">
        <RevCarousel
          :alternative-button-label="i18n(translations.alternativeController)"
          :alternative-controller="i18n(translations.alternativeController)"
          :alternative-current="i18n(translations.alternativeCurrent)"
          :alternative-next="i18n(translations.alternativeNext)"
          :alternative-previous="i18n(translations.alternativePrevious)"
          class="mt-16"
          :controller-images="counterOfferData.imagesList"
          controller-position="bottom"
          controller-variant="dots"
          current-index-id="counter-offer-carousel"
          :display-navigation="true"
        >
          <RevIllustration
            v-for="image in counterOfferData.imagesList"
            :key="image.src"
            :alt="`Counter offer image ${image.id}`"
            class="rounded-xs mt-8 w-full"
            :height="720"
            :src="image.src"
            :width="842"
          />
        </RevCarousel>
      </div>
      <RevList v-if="hasPdf" class="mt-12 px-6" :has-dividers="false">
        <RevListItemInteractive
          v-for="{
            id: docId,
            src,
            displayName,
          } in counterOfferData.documentsList"
          :key="docId"
        >
          <template #label>
            <RevLink
              class="block truncate text-left [direction:rtl]"
              :to="src"
              :underlined="false"
            >
              {{ displayName }}
            </RevLink>
          </template>
        </RevListItemInteractive>
      </RevList>

      <div class="grid grid-cols-2 gap-16 pb-28">
        <div class="col-span-2">
          <RevInputTextarea
            v-if="hasComment"
            id="comment"
            disabled
            :label="i18n(translations.commentLabel)"
            :model-value="counterOfferData.comment"
          />
        </div>

        <RevInputText
          id="newPrice"
          disabled
          :label="i18n(translations.newPrice)"
          :model-value="counterOfferData.formattedCounterOfferPrice"
        />
        <RevInputText
          id="initialPrice"
          disabled
          :label="i18n(translations.initialPrice)"
          :model-value="counterOfferData.formattedPrice"
        />
      </div>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { RefurbisherCounterOfferDetails } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCarousel } from '@ds/components/Carousel'
import { RevDrawer } from '@ds/components/Drawer'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInputText } from '@ds/components/InputText'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevLink } from '@ds/components/Link'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'

import { counterOfferAdapter } from '@backmarket/nuxt-layer-buyback/adapters/counterOfferAdapter'
import { COUNTEROFFER_ROLES } from '@backmarket/nuxt-layer-buyback/constants'
import modalBuybackTranslations from '@backmarket/nuxt-layer-buyback/utils/Modal.translations'
import MODAL_NAMES from '@backmarket/nuxt-layer-buyback/utils/modals'

import translations from './CounterOfferSummaryModal.translations'
import staffTranslations from './StaffOrder.translations'

const props = withDefaults(
  defineProps<{
    counterOffer: RefurbisherCounterOfferDetails | null
    isStaff: boolean
    name?: string
  }>(),
  { name: MODAL_NAMES.VIEW_COUNTER_OFFER },
)

const emit = defineEmits(['is-loaded', 'reload-sales', 'reject', 'close'])

const i18n = useI18n()

const title = computed(() => {
  const author = props.counterOffer?.authorRole
  if (author === COUNTEROFFER_ROLES.ADMIN) {
    return i18n(staffTranslations.counterOfferModalStaffAssessment)
  }

  return props.isStaff
    ? i18n(staffTranslations.counterOfferModalAssessment)
    : i18n(translations.title)
})

const counterOfferData = computed(() =>
  props.counterOffer ? counterOfferAdapter(props.counterOffer) : null,
)

const hasImages = computed(
  () =>
    counterOfferData.value?.imagesList.length &&
    counterOfferData.value.imagesList.length > 0,
)
const hasPdf = computed(
  () =>
    counterOfferData.value?.documentsList.length &&
    counterOfferData.value.documentsList.length > 0,
)
const hasComment = computed(() => counterOfferData.value?.comment)
</script>
